import { BLACK_COLOR, DEFAULT_COLORS, WHITE_COLOR } from '@shared/constants'
import { Editor, useEditorState } from '@tiptap/react'
import { Button, ColorPicker, Flex, Popover, Tooltip } from 'antd'
import { Brush2 } from 'iconsax-react'
import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'

import type { ColorPickerProps } from 'antd'

type Props = {
  editor: Editor
}

export const TextColorButton = ({ editor }: Props) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const { styles } = useStyles()

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const colorAttr = ctx.editor.getAttributes('textStyle').color

      return {
        color: colorAttr || ''
      }
    }
  })

  const currentColor = editorState.color || BLACK_COLOR

  const onColorChange: ColorPickerProps['onChange'] = (color) => {
    const hexColor = color.toHexString()
    editor.chain().focus().setColor(hexColor).run()
  }

  const onClear = () => {
    editor.chain().focus().unsetColor().run()
    setOpen(false)
  }

  const onClose = () => {
    setOpen(false)
  }

  const colorPickerContent = (
    <Flex vertical className={styles.colorPickerContent}>
      <ColorPicker
        value={currentColor}
        onChange={onColorChange}
        format="hex"
        showText
        disabledAlpha
        presets={[
          {
            label: t('editor.default'),
            colors: [BLACK_COLOR, WHITE_COLOR]
          },
          {
            label: t('editor.common'),
            colors: DEFAULT_COLORS
          }
        ]}
      />
      <Flex gap="8px" className={styles.buttonContainer}>
        <Button size="small" onClick={onClear} className={styles.button}>
          Clear
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={onClose}
          className={styles.button}
        >
          Done
        </Button>
      </Flex>
    </Flex>
  )

  return (
    <Tooltip title={t('editor.color')}>
      <Popover
        content={colorPickerContent}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottom"
      >
        <Button
          icon={<Brush2 size={14} />}
          type={editorState.color ? 'primary' : 'default'}
        />
      </Popover>
    </Tooltip>
  )
}
